import { useEffect, useState, useCallback } from 'react'
import {
  User,
  getIdTokenResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs, deleteDoc, updateDoc } from 'firebase/firestore'
import { auth, db, googleProvider, hasValidFirebaseEnv } from '@/lib/firebase'
import { AdminProfile, AdminRole } from '@/types'

// Hardcoded super admin emails
const SUPER_ADMIN_EMAILS = ['khafidbachtiar@gmail.com']

interface AuthState {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  adminProfile: AdminProfile | null
  tokenClaims: Record<string, unknown> | null
  error: string | null
  signInWithGoogle: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signOutUser: () => Promise<void>
  // RBAC Methods
  canAccessSettings: boolean
  canManageAdmins: boolean
  canEditContent: boolean
  canEditProducts: boolean
  canEditArticles: boolean
  canEditClasses: boolean
  canEditBanners: boolean
  canViewAnalytics: boolean
  canViewUsers: boolean
  canViewSupport: boolean
}

// Permission helpers
export function getRolePermissions(role: AdminRole | string | undefined) {
  const permissions = {
    superadmin: {
      canAccessSettings: true,
      canManageAdmins: true,
      canEditContent: true,
      canEditProducts: true,
      canEditArticles: true,
      canEditClasses: true,
      canEditBanners: true,
      canViewAnalytics: true,
      canViewUsers: true,
      canViewSupport: true,
    },
    admin: {
      canAccessSettings: true,
      canManageAdmins: false, // Admin cannot manage other admins
      canEditContent: true,
      canEditProducts: true,
      canEditArticles: true,
      canEditClasses: true,
      canEditBanners: true,
      canViewAnalytics: true,
      canViewUsers: true,
      canViewSupport: true,
    },
    editor: {
      canAccessSettings: false,
      canManageAdmins: false,
      canEditContent: true,
      canEditProducts: true,
      canEditArticles: true,
      canEditClasses: true,
      canEditBanners: true,
      canViewAnalytics: false,
      canViewUsers: false,
      canViewSupport: false,
    },
    viewer: {
      canAccessSettings: false,
      canManageAdmins: false,
      canEditContent: false,
      canEditProducts: false,
      canEditArticles: false,
      canEditClasses: false,
      canEditBanners: false,
      canViewAnalytics: true,
      canViewUsers: true,
      canViewSupport: true,
    },
  }
  
  return permissions[(role as AdminRole) || 'viewer']
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null)
  const [tokenClaims, setTokenClaims] = useState<Record<string, unknown> | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setLoading(true)
      setUser(nextUser)
      setError(null)

      if (!nextUser) {
        setIsAdmin(false)
        setIsSuperAdmin(false)
        setAdminProfile(null)
        setTokenClaims(null)
        setLoading(false)
        return
      }

      try {
        // Check for hardcoded superadmin
        const isHardcodedSuperAdmin = SUPER_ADMIN_EMAILS.includes(nextUser.email || '')
        
        // Get token result first (this doesn't require Firestore permissions)
        let tokenResult
        try {
          tokenResult = await getIdTokenResult(nextUser, true) // Force refresh
        } catch (tokenError) {
          console.error('Token error:', tokenError)
          setError('Failed to verify authentication token')
          setIsAdmin(false)
          setIsSuperAdmin(false)
          setLoading(false)
          return
        }

        const claims = tokenResult.claims as Record<string, unknown>
        
        // Try to get admin doc - but don't fail if permission denied
        let adminData: AdminProfile | null = null
        
        try {
          const adminDoc = await getDoc(doc(db, 'adminUsers', nextUser.uid))
          if (adminDoc.exists()) {
            adminData = { id: adminDoc.id, ...adminDoc.data() } as AdminProfile
          }
        } catch (e: any) {
          console.warn('Could not fetch admin doc:', e?.message || e)
          // Don't throw - continue with claims-based check
        }

        // Auto-create superadmin profile for hardcoded emails if not exists
        if (isHardcodedSuperAdmin && !adminData) {
          try {
            const newSuperAdmin: Omit<AdminProfile, 'id'> = {
              uid: nextUser.uid,
              email: nextUser.email || '',
              displayName: nextUser.displayName || 'Super Admin',
              role: 'superadmin',
              status: 'active',
              scopes: ['global'],
              updatedAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            }
            await setDoc(doc(db, 'adminUsers', nextUser.uid), newSuperAdmin)
            adminData = { id: nextUser.uid, ...newSuperAdmin } as AdminProfile
            console.log('Created superadmin profile for:', nextUser.email)
          } catch (createError: any) {
            console.warn('Could not create superadmin profile:', createError?.message)
            // Continue anyway - we'll use the hardcoded check
          }
        }

        // Determine role and admin status
        const roleFromClaims = claims.role as string
        const roleFromDoc = adminData?.role
        
        // Priority: Firestore doc > Claims > Default
        let effectiveRole: string = roleFromDoc || roleFromClaims || ''
        
        // Hardcoded superadmin override
        if (isHardcodedSuperAdmin) {
          effectiveRole = 'superadmin'
        }

        const isSuperAdminRole = effectiveRole === 'superadmin' || isHardcodedSuperAdmin
        const hasAdminAccess = effectiveRole && ['superadmin', 'admin', 'editor', 'viewer'].includes(effectiveRole)

        // If no admin data and no claims and not hardcoded, user is not an admin
        if (!adminData && !roleFromClaims && !isHardcodedSuperAdmin) {
          setError('You do not have admin access. Please contact your administrator.')
          setIsAdmin(false)
          setIsSuperAdmin(false)
          setAdminProfile(null)
          setTokenClaims(claims)
          setLoading(false)
          return
        }

        setTokenClaims(claims)
        setAdminProfile(
          adminData ?? {
            uid: nextUser.uid,
            email: nextUser.email ?? undefined,
            displayName: nextUser.displayName ?? undefined,
            role: effectiveRole as AdminRole,
            status: 'active',
            scopes: isSuperAdminRole ? ['global'] : ['content'],
          }
        )
        setIsAdmin(hasAdminAccess || !!adminData || isHardcodedSuperAdmin)
        setIsSuperAdmin(isSuperAdminRole)
      } catch (authError: any) {
        console.error('Auth error:', authError)
        setError(
          authError instanceof Error
            ? authError.message
            : 'Failed to validate admin session.'
        )
        setIsAdmin(false)
        setIsSuperAdmin(false)
        setAdminProfile(null)
      } finally {
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const signInWithGoogle = async () => {
    try {
      setError(null)

      if (!hasValidFirebaseEnv) {
        throw new Error(
          'Firebase configuration is missing. Please check your environment variables.'
        )
      }

      await signInWithPopup(auth, googleProvider)
    } catch (signInError: any) {
      console.error('Sign in error:', signInError)
      
      // Handle specific Firebase errors
      if (signInError.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.')
      } else if (signInError.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked. Please allow pop-ups for this site.')
      } else if (
        signInError.code === 'auth/web-storage-unsupported' ||
        /sessionStorage|initial state|storage/i.test(String(signInError?.message ?? ''))
      ) {
        // Pesan ini datang dari halaman Google, bukan dari SDK Firebase: browser
        // memblokir penyimpanan data situs sehingga alur OAuth tak bisa jalan.
        setError(
          'Browser memblokir penyimpanan data situs, jadi login Google tidak bisa diselesaikan. ' +
          'Izinkan cookie/data situs untuk halaman ini, atau pakai login email & password di bawah.'
        )
      } else {
        setError(
          signInError instanceof Error
            ? signInError.message
            : 'Google Sign-In failed. Please try again.'
        )
      }
    }
  }

  // Jalur cadangan yang TIDAK melewati halaman Google sama sekali, jadi
  // kebal terhadap browser yang memblokir cookie/sessionStorage untuk
  // accounts.google.com. Peran tetap digerbangi adminUsers + custom claim.
  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null)
      if (!hasValidFirebaseEnv) {
        throw new Error(
          'Firebase configuration is missing. Please check your environment variables.'
        )
      }
      await signInWithEmailAndPassword(auth, email.trim(), password)
    } catch (e: any) {
      console.error('Email sign in error:', e)
      const code = e?.code as string | undefined
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' ||
          code === 'auth/user-not-found') {
        setError('Email atau password salah.')
      } else if (code === 'auth/too-many-requests') {
        setError('Terlalu banyak percobaan. Coba lagi beberapa menit lagi.')
      } else if (code === 'auth/user-disabled') {
        setError('Akun ini dinonaktifkan.')
      } else {
        setError(e instanceof Error ? e.message : 'Login gagal. Coba lagi.')
      }
    }
  }

  const signOutUser = async () => {
    try {
      await signOut(auth)
      setError(null)
    } catch (e) {
      console.error('Sign out error:', e)
    }
  }

  // Calculate permissions based on role
  const permissions = getRolePermissions(adminProfile?.role)

  return {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isSuperAdmin,
    adminProfile,
    tokenClaims,
    error,
    signInWithGoogle,
    signInWithEmail,
    signOutUser,
    ...permissions,
  }
}

// Admin Management Hooks
export function useAdminUsers() {
  const [admins, setAdmins] = useState<AdminProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isSuperAdmin } = useAuth()

  const loadAdmins = useCallback(async () => {
    if (!isSuperAdmin) {
      setLoading(false)
      return
    }
    
    setError(null)
    setLoading(true)
    
    try {
      const snapshot = await getDocs(collection(db, 'adminUsers'))
      const adminList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as AdminProfile))
      setAdmins(adminList)
    } catch (e: any) {
      console.error('Failed to load admin users:', e)
      setError(e?.message || 'Failed to load admin users')
    } finally {
      setLoading(false)
    }
  }, [isSuperAdmin])

  useEffect(() => {
    loadAdmins()
  }, [loadAdmins])

  const createAdmin = async (email: string, role: AdminRole, displayName?: string) => {
    if (!isSuperAdmin) throw new Error('Only superadmin can create admins')
    
    // Generate UID from email (will be updated when user first logs in)
    const tempUid = email.toLowerCase().replace(/[^a-z0-9]/g, '_')
    
    const newAdmin: Omit<AdminProfile, 'id'> = {
      uid: tempUid,
      email,
      displayName: displayName || email.split('@')[0],
      role,
      status: 'invited',
      scopes: role === 'superadmin' ? ['global'] : ['content', 'commerce'],
      updatedAt: serverTimestamp(),
    }
    
    await setDoc(doc(db, 'adminUsers', tempUid), newAdmin)
    await loadAdmins()
    return tempUid
  }

  const updateAdminRole = async (uid: string, role: AdminRole) => {
    if (!isSuperAdmin) throw new Error('Only superadmin can update admin roles')
    
    await updateDoc(doc(db, 'adminUsers', uid), {
      role,
      updatedAt: serverTimestamp(),
    })
    await loadAdmins()
  }

  const removeAdmin = async (uid: string) => {
    if (!isSuperAdmin) throw new Error('Only superadmin can remove admins')
    
    await deleteDoc(doc(db, 'adminUsers', uid))
    await loadAdmins()
  }

  return {
    admins,
    loading,
    error,
    createAdmin,
    updateAdminRole,
    removeAdmin,
    refresh: loadAdmins,
  }
}

// Permission check hook
export function usePermission() {
  const { adminProfile, isSuperAdmin } = useAuth()
  
  const hasPermission = useCallback((requiredRole: AdminRole | AdminRole[]): boolean => {
    if (isSuperAdmin) return true
    if (!adminProfile?.role) return false
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(adminProfile.role as AdminRole)
    }
    
    const roleHierarchy: Record<AdminRole, number> = {
      viewer: 0,
      editor: 1,
      admin: 2,
      superadmin: 3,
    }
    
    return roleHierarchy[adminProfile.role as AdminRole] >= roleHierarchy[requiredRole]
  }, [adminProfile?.role, isSuperAdmin])

  const canManageUser = useCallback((targetRole: AdminRole): boolean => {
    // Superadmin can manage anyone
    if (isSuperAdmin) return true
    // Admin can only manage editors and viewers
    if (adminProfile?.role === 'admin') {
      return targetRole === 'editor' || targetRole === 'viewer'
    }
    return false
  }, [adminProfile?.role, isSuperAdmin])

  return {
    hasPermission,
    canManageUser,
    role: adminProfile?.role || 'viewer',
    isSuperAdmin,
  }
}
